/* ==========================================================================
   Bedrock — Barre d'achat collante (bdr-sticky-atc)
   Résout les options via le Section Rendering API sans charger la liste complète,
   puis poste directement vers la route panier localisée fournie par Liquid.
   ========================================================================== */

(function () {
  'use strict';

  class BdrStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-bdr-form]');
      this.variantField = this.querySelector('[data-bdr-variant-select]');
      this.optionSelects = Array.from(this.querySelectorAll('[data-bdr-option-select]'));
      this.qtyField = this.querySelector('[data-bdr-qty]');
      this.submitBtn = this.querySelector('[data-bdr-submit]');
      this.submitLabel = this.querySelector('[data-bdr-submit-label]');
      this.messageEl = this.querySelector('[data-bdr-message]');
      this.priceCurrentEl = this.querySelector('[data-bdr-price-current]');
      this.priceCompareEl = this.querySelector('[data-bdr-price-compare]');
      this.imageEl = this.querySelector('.bdr-sticky-atc__img');

      const dataScript = this.querySelector('[data-bdr-sticky-atc-variant]');
      try {
        this.currentVariant = dataScript ? JSON.parse(dataScript.textContent) : null;
      } catch (error) {
        this.currentVariant = null;
      }

      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
      this.productUrl = this.dataset.productUrl || window.location.pathname;
      this.sectionId = this.dataset.sectionId;
      this.targetSelector = this.dataset.targetSelector || '[name="add"]';
      this.scrollThreshold = parseInt(this.dataset.scrollThreshold, 10) || 600;
      this.state = 'idle';
      this.resetTimer = null;

      if (this.form) {
        this.onSubmit = this.handleSubmit.bind(this);
        this.form.addEventListener('submit', this.onSubmit);
      }

      if (this.optionSelects.length) {
        this.onOptionChange = this.handleOptionChange.bind(this);
        this.optionSelects.forEach((select) => select.addEventListener('change', this.onOptionChange));
      }

      this.setupVisibilityTracking();

      this.onSectionLoad = (event) => {
        if (!event.target || !event.target.contains(this)) return;
        this.setupVisibilityTracking();
      };
      document.addEventListener('shopify:section:load', this.onSectionLoad);
    }

    disconnectedCallback() {
      if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit);
      if (this.optionSelects && this.onOptionChange) {
        this.optionSelects.forEach((select) => select.removeEventListener('change', this.onOptionChange));
      }
      if (this.optionAbortController) this.optionAbortController.abort();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onSectionLoad) document.removeEventListener('shopify:section:load', this.onSectionLoad);
      if (this.resetTimer) clearTimeout(this.resetTimer);
    }

    setupVisibilityTracking() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        this.onScroll = null;
      }

      const target = document.querySelector(this.targetSelector);

      if (target && 'IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrolledPast = entry.boundingClientRect.bottom < 0;
              this.toggleVisible(!entry.isIntersecting && scrolledPast);
            });
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(target);
        return;
      }

      this.onScroll = this.throttle(() => {
        this.toggleVisible(window.scrollY > this.scrollThreshold);
      }, 120);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    throttle(fn, wait) {
      let last = 0;
      let timer = null;
      return function throttled() {
        const now = Date.now();
        const remaining = wait - (now - last);
        if (remaining <= 0) {
          last = now;
          fn();
        } else if (!timer) {
          timer = setTimeout(() => {
            last = Date.now();
            timer = null;
            fn();
          }, remaining);
        }
      };
    }

    toggleVisible(show) {
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.toggleAttribute('inert', !show);
    }

    async handleOptionChange() {
      if (!this.sectionId || !this.optionSelects.length) return;

      if (this.optionAbortController) this.optionAbortController.abort();
      this.optionAbortController = new AbortController();
      this.optionSelects.forEach((select) => select.setAttribute('aria-busy', 'true'));
      if (this.submitBtn) this.submitBtn.disabled = true;

      const optionValues = this.optionSelects.map((select) => select.value).filter(Boolean);
      const requestUrl = new URL(this.productUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);
      requestUrl.searchParams.set('option_values', optionValues.join(','));

      try {
        const response = await fetch(requestUrl.toString(), {
          headers: { Accept: 'text/html' },
          signal: this.optionAbortController.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextSection = html.querySelector('.bdr-sticky-atc');
        const currentSection = this.closest('.bdr-sticky-atc');
        if (!nextSection || !currentSection) throw new Error('Section Bedrock introuvable');

        currentSection.replaceWith(nextSection);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.optionSelects.forEach((select) => select.removeAttribute('aria-busy'));
        if (this.submitBtn) this.submitBtn.disabled = !this.currentVariant || !this.currentVariant.available;
        if (this.messageEl) this.messageEl.textContent = this.dataset.labelError;
      }
    }

    formatMoney(cents) {
      if (window.Bedrock && typeof window.Bedrock.money === 'function') {
        return window.Bedrock.money(cents);
      }
      return String(Number(cents));
    }

    setState(state, message) {
      this.state = state;
      this.setAttribute('data-state', state);

      if (this.resetTimer) {
        clearTimeout(this.resetTimer);
        this.resetTimer = null;
      }

      if (this.submitLabel) {
        if (state === 'loading') {
          this.submitLabel.textContent = this.dataset.labelAdding;
        } else if (state === 'success') {
          this.submitLabel.textContent = this.dataset.labelAdded;
          this.resetTimer = setTimeout(() => this.setState('idle'), 1800);
        } else {
          const available = this.currentVariant ? this.currentVariant.available : true;
          this.submitLabel.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldOut;
        }
      }

      if (this.messageEl) this.messageEl.textContent = message || '';

      if (this.submitBtn) this.submitBtn.disabled = state === 'loading';
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.state === 'loading' || !this.variantField) return;

      const variantId = this.currentVariant ? this.currentVariant.id : this.variantField.value;
      const quantity = this.qtyField ? parseInt(this.qtyField.value, 10) || 1 : 1;

      if (!variantId) return;

      this.setState('loading');

      try {
        const response = await fetch(this.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] }),
        });

        const data = await response.json();

        if (!response.ok) {
          const message = (data && (data.description || data.message)) || this.dataset.labelError;
          this.setState('error', message);
          return;
        }

        this.setState('success');
        document.dispatchEvent(
          new CustomEvent('bdr:cart:update', { bubbles: true, detail: { source: 'bdr-sticky-atc', cart: data } })
        );
        document.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'bdr-sticky-atc', cart: data } })
        );
        if (typeof window.publishUpdate === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.publishUpdate(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bdr-sticky-atc', cartData: data });
        }
        this.tryOpenCartDrawer();
      } catch (error) {
        this.setState('error', this.dataset.labelError);
      }
    }

    tryOpenCartDrawer() {
      try {
        const drawer = document.querySelector('cart-drawer, [data-bdr-cart-drawer], #cart-drawer, #CartDrawer');
        if (!drawer) return;
        if (typeof drawer.open === 'function') {
          drawer.open();
        } else if (typeof drawer.show === 'function') {
          drawer.show();
        } else if (typeof drawer.showModal === 'function') {
          drawer.showModal();
        }
      } catch (error) {
        /* pas de tiroir panier disponible : on ignore silencieusement */
      }
    }
  }

  if (!customElements.get('bdr-sticky-atc')) {
    customElements.define('bdr-sticky-atc', BdrStickyAtc);
  }
})();
